import angular from 'angular';
import csmCommonWeb from 'csm-common-web';
import BooksSvc from '../../util/BooksSvc/BooksSvc';
import OverdrawnItemsDialog from '../../dialogs/OverdrawnItemsDialog/OverdrawnItemsDialog';
import UnpaidPurchaseOrdersDialog from '../../dialogs/UnpaidPurchaseOrdersDialog/UnpaidPurchaseOrdersDialog';

export default angular
  .module('books.util.InvoiceService', [
    csmCommonWeb,
    BooksSvc,
    OverdrawnItemsDialog,
    UnpaidPurchaseOrdersDialog
  ])
  .factory('InvoiceService', InvoiceServiceFactory).name;

/* @ngInject */
function InvoiceServiceFactory(
  $q,
  AccountType,
  Alert,
  BooksSvc,
  OverdrawnItemsDialog,
  UnpaidPurchaseOrdersDialog
) {
  function InvoiceService() {
    /*--------------------------------------*/
    /* Map of item accounts and the amounts */
    /* drawn on them on loaded invoices.    */
    /*--------------------------------------*/
    var mAccountsMap = {};

    /***************************************************************************
     * checkItems */
    /**
     * Displays an alert if no items have been added to the invoice and returns
     * false, otherwise returns true.
     *
     * @param   items    Array of bill items
     * @return  boolean  True if items exist, false otherwise.
     ***************************************************************************/
    this.checkItems = function (items) {
      if (items.length < 1) {
        Alert.error('Invoices must have at least 1 Item.', 'Missing Data');
        return false;
      }
      return true;
    };

    /***************************************************************************
     * checkItemsAccount */
    /**
     * Displays an alert if any product items (that are not inventory tracked)
     * are missing accounts & return false, otherwise returns true.
     * need accounts.
     *
     * @param   items    Array of items
     * @return  boolean  True if all product items have accounts, otherwise false.
     ***************************************************************************/
    this.checkItemsAccount = function (items) {
      if (
        items.some(function (item) {
          return !(item.inventoryID || item.inventoryProductID)
            ? item.productID > 0 && !item.accountID
            : false;
        })
      ) {
        Alert.error(
          'One or more product items are missing Account data.',
          'Missing Account'
        );
        return false;
      }
      return true;
    };

    /***************************************************************************
     * checkForOverdrawnAccounts */
    /**
     * Takes a list of invoice items and, in a modal, displays the items with Trust
     * accounts that will cause an overdraw allowing the user to continue saving or
     * return to the check. Returns a promise that will resolve when Continue Save
     *  is clicked, and will reject when Return to Check is clicked.
     *
     * @param   invoiceItems   An array of valid invoice items.
     * @return                 Promise to listen to
     ***************************************************************************/
    this.checkForOverdrawnAccounts = function (invoiceItems) {
      /*--------------------------------------------------------------------*/
      /* Get all the account IDs (for items with accounts) on this invoice. */
      /*--------------------------------------------------------------------*/
      var invoiceIDs = invoiceItems.map(function (item) {
        return item.accountID ? item.accountID : null;
      });

      /*-------------------------------------------------*/
      /* Reduce list of IDs to non null unique IDs only. */
      /*-------------------------------------------------*/
      var uniqueAccountIDs = invoiceIDs.filter(function (value, index, self) {
        return value ? self.indexOf(value) === index : false;
      });

      /*---------------------------------------------------------------*/
      /* Store the service call to get account data for each unique ID */
      /*---------------------------------------------------------------*/
      var promises = [];
      angular.forEach(uniqueAccountIDs, function (id) {
        promises.push(BooksSvc.getAccount(id));
      });

      /*-----------------------------------------------------*/
      /* Get account data for each of the unique account IDs */
      /*-----------------------------------------------------*/
      return $q.all(promises).then(function (responses) {
        /*---------------------------------------------*/
        /* Trust accounts to be modified by this check */
        /*---------------------------------------------*/
        var accountConfigs = [];

        /*----------------------------------------------*/
        /* For each trust account, get the total amount */
        /* that will be drawn on it for this save.      */
        /*----------------------------------------------*/
        angular.forEach(responses, function (response) {
          if (response.data.type == AccountType.TRUST) {
            var trustTotal = 0;

            /*---------------------------------------*/
            /* For each Trust account, get total     */
            /* amount to be drawn on it upon saving. */
            /*---------------------------------------*/
            angular.forEach(invoiceItems, function (item) {
              if (item.accountID == response.data.accountID)
                trustTotal += item.total;
            });

            /*---------------------------------------------*/
            /* Push the account info to the config object. */
            /*---------------------------------------------*/
            accountConfigs.push({
              accountName: response.data.accountName,
              accountNumber: response.data.accountNumber,
              accountID: response.data.accountID,
              balance: response.data.currentBalance,
              total: trustTotal
            });
          }
        });

        /*-----------------------------------*/
        /* Get list of accounts that will be */
        /* overdrawn if the check is saved.  */
        /*-----------------------------------*/
        var overdrawnAccounts = accountConfigs.filter(function (config) {
          if (mAccountsMap[config.accountID])
            config.expectedBalance =
              config.balance - (config.total - mAccountsMap[config.accountID]);
          else config.expectedBalance = config.balance - config.total;
          return (
            config.expectedBalance < 0 &&
            config.expectedBalance < config.balance
          );
        });

        /*-----------------------------------------------------*/
        /* Display accounts that will overdraw in modal dialog */
        /*-----------------------------------------------------*/
        if (overdrawnAccounts.length)
          return OverdrawnItemsDialog.show(overdrawnAccounts);
        else return;
      });
    };

    /***************************************************************************
     * getItemsTotal */
    /**
     * Returns the total amount on all invoice items.
     *
     * @param    items    Array of Purchase Orders
     * @return   Number   Sum total
     ***************************************************************************/
    this.getItemsTotal = function (items) {
      return (items || []).reduce(function (sum, item) {
        return (item.total || 0) + sum;
      }, 0);
    };

    /***************************************************************************
     * handleClosePOs */
    /**
     * Sends an array of PO IDs to the UnpaidPurchaseOrdersDialog modal dialog
     *
     * @param   items   Array of Purchase Orders
     * @param   date    Date to use when closing the unpaid POs
     * @return          Promise to listen to
     ***************************************************************************/
    this.handleClosePOs = function (items, date) {
      var detailIDs = items
        .filter(function (item) {
          return !!item.poItemID;
        })
        .map(function (item) {
          return item.poItemID;
        });

      return UnpaidPurchaseOrdersDialog.show(date, detailIDs);
    };

    /***************************************************************************
     * saveAccountsMap */
    /**
     * Saves a map of accounts and the total amounts drawn on them. The map will
     * be checked when making changes to a previously saved invoice to notify the
     * user of any additional overdraw to trust accounts. This method should
     * be called when loading an invoice to map the accounts that will be
     * referenced upon saving.
     *
     * @param   items   Array of items loaded from a save invoice.
     ***************************************************************************/
    this.saveAccountsMap = function (invoiceItems) {
      mAccountsMap = {};
      angular.forEach(invoiceItems, function (item) {
        if (!mAccountsMap.hasOwnProperty(item.accountID))
          mAccountsMap[item.accountID] = 0;
        mAccountsMap[item.accountID] += item.total;
      });
    };
  }

  return new InvoiceService();
}
